import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import Toast from "../Toast";
import "./HomePage.css";

// ─── Static data ─────────────────────────────────────────────────────────────

const TRUST_LOGOS = ["EventPro India", "Campus Click", "Expo Lens", "ShutterHive", "MomentCraft", "Royal Frames", "Instants Studio", "Pixel Stories"];

const STEPS = [
  { n: "01", title: "Bulk Upload Photos", desc: "Upload thousands of photos at once. Our pipeline processes 5,000+ photos in minutes — with smart organisation, thumbnail loading, and watermarks applied automatically." },
  { n: "02", title: "AI Organises Everything", desc: "Face recognition runs across every photo. Stage albums are created by session type. Tags applied. Branding added. All without you touching a single file manually." },
  { n: "03", title: "Guests Find Their Moments", desc: "Each attendee takes a selfie. AI scans the entire event and delivers a personalized gallery of only their photos — in seconds, across thousands of images." },
  { n: "04", title: "Share, Select & Preserve", desc: "Guests share via the smart event feed. Organizers shortlist for albums. Memories are archived forever. Your profile earns you enquiries from the next event." },
];

const WHY_CARDS = [
  { icon: "⏱", title: "Save Hours on Every Event", desc: "No more manual sorting, WhatsApp chains, or screenshot-based album selection. What your whole team spent a day on now runs while you sleep." },
  { icon: "🚀", title: "Deliver Within Hours, Not Days", desc: "Photos reach guests hours after an event ends. The photographer who delivers first is the one clients remember, recommend, and rebook." },
  { icon: "🎨", title: "Your Brand on Every Gallery", desc: "Watermarks, branded galleries, and a public photographer profile — every touchpoint carries your studio name, not a generic platform's." },
  { icon: "❤️", title: "Guests Who Remember You Forever", desc: "When someone finds their photos with one selfie among 10,000 images — that is the moment they tell every future event organizer who their photographer must be." },
];

const FEAT_PHONE_CELLS = [true, true, false, false, true, true];

const INDUSTRIES = [
  { emoji: "💍", title: "Weddings",          desc: "Personalized galleries per guest. Album selection without screenshots." },
  { emoji: "🎤", title: "Conferences",       desc: "Stage albums by session. Speaker and attendee galleries, auto-sorted." },
  { emoji: "🎭", title: "Cultural Events",   desc: "Performance albums. Feed sharing keeps the community engaged." },
  { emoji: "🎓", title: "College Fests",     desc: "Selfie search across thousands of students. Smart feed keeps energy alive." },
  { emoji: "🏢", title: "Corporate Events",  desc: "Branded galleries. Privacy controls for internal event photos." },
  { emoji: "🏆", title: "Award Ceremonies",  desc: "Stage albums by category. Winners find their moment immediately." },
  { emoji: "🎪", title: "Public Gatherings", desc: "Scale to tens of thousands of photos. Smart delivery at every size." },
  { emoji: "🎵", title: "Performances",      desc: "Artist and audience galleries. Shareable moments that grow your reach." },
];

const COMPARE_ROWS = [
  { feature: "AI Selfie / Face Recognition", ee: true, gd: false,          gallery: true,       pen: false       },
  { feature: "Personalized Guest Galleries", ee: true, gd: false,          gallery: true,       pen: false       },
  { feature: "Smart Event Feed",             ee: true, gd: false,          gallery: false,      pen: false       },
  { feature: "Feed-Based Photo Sharing",     ee: true, gd: false,          gallery: false,      pen: false       },
  { feature: "Dynamic Privacy Controls",     ee: true, gd: false,          gallery: "Limited",  pen: false       },
  { feature: "Album Selection Workflow",     ee: true, gd: false,          gallery: "Partial",  pen: false       },
  { feature: "Stage-Organized Albums",       ee: true, gd: false,          gallery: "Limited",  pen: false       },
  { feature: "Watermark & Branding",         ee: true, gd: false,          gallery: "Partial",  pen: false       },
  { feature: "Lifetime Archived Storage",    ee: true, gd: "Plan-based",   gallery: "Limited",  pen: "At risk"   },
  { feature: "Photographer Portfolio",       ee: true, gd: false,          gallery: "Limited",  pen: false       },
  { feature: "Direct Client Enquiries",      ee: true, gd: false,          gallery: false,      pen: false       },
  { feature: "Integrated Event Ecosystem",   ee: true, gd: false,          gallery: false,      pen: false       },
];

const TESTIMONIALS = [
  { name: "Arjun K.",  role: "Wedding Photographer, Bengaluru", text: "Our clients found their photos before we even left the venue. The album workflow alone saved us a full day of back-and-forth on every event.", initials: "AK" },
  { name: "Reema M.",  role: "Event Manager, Mumbai",            text: "The smart feed turned our conference into a living memory. Attendees were sharing and engaging two days after it ended. Never seen engagement like that.", initials: "RM" },
  { name: "Kiran S.",  role: "Photography Studio, Delhi",        text: "First platform that made our event galleries feel truly premium. And the portfolio enquiries that came through afterward were completely unexpected — and very welcome.", initials: "KS" },
];

const FAQS = [
  { q: "How do guests find their photos?", a: "Guests upload a selfie through the Easy Events platform. AI scans every photo in the event and delivers a personalized gallery of only their photos in seconds — across thousands of images, group shots, and candid moments." },
  { q: "How does the credit system work?", a: "1 credit = 1 photo processed and stored. Credits are deducted only when a photo is successfully uploaded — not for failed uploads or duplicates. Every new account starts with 100 free credits, no card needed." },
  { q: "Can I add my own branding and watermarks?", a: "Yes. You can apply custom watermarks to all event photos. Your public photographer profile carries your studio name and branding throughout the platform — every touchpoint reinforces your identity, not ours." },
  { q: "Can guests download their photos?", a: "Download permissions are fully controlled by the photographer. You decide whether guests can download, at what quality, and for how long — protecting your work while still delivering a great guest experience." },
  { q: "Is my data and my clients' photos secure?", a: "All photos are stored with encrypted access controls. Attendees can only see photos they are matched to, and privacy settings give each person full control over what others can see of their content." },
  { q: "What does 'lifetime storage' actually mean?", a: "Every event you upload is archived permanently — no expiry dates, no re-upload required, no storage subscription needed. Your clients can access their wedding or event photos years from now, exactly as delivered." },
];

const PRICE_TIERS = [
  { range: "1K – 5K photos",   price: "₹5",   best: false },
  { range: "5K – 10K photos",  price: "₹4.5", best: false },
  { range: "10K – 25K photos", price: "₹4",   best: true  },
  { range: "25K – 50K photos", price: "₹3.5", best: false },
  { range: "50K+ photos",      price: "₹3",   best: false },
];

const PRICE_PERKS = [
  "Credits on successful uploads only",
  "Lifetime storage included",
  "Bulk upload optimised",
  "Reseller coupons available",
  "No subscription lock-in",
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function HomePage() {
  const [otpSent, setOtpSent]       = useState(false);
  const [countdown, setCountdown]   = useState(300);
  const [otp, setOtp]               = useState(["", "", "", ""]);
  const [phone, setPhone]           = useState("");
  const [loading, setLoading]       = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [toast, setToast]           = useState({ open: false, type: "info", message: "" });
  const [openFaq, setOpenFaq]       = useState(null);
  const loginRef = useRef(null);
  const history  = useHistory();

  const scrollToLogin = () =>
    loginRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const getApiUrl = (ep) =>
    process.env.NODE_ENV === "development"
      ? `/Stage/${ep}`
      : `${process.env.REACT_APP_API_BASE_URL}/${ep}`;

  const handleChange = (e) => {
    if (/^\d{0,10}$/.test(e.target.value)) setPhone(e.target.value);
  };

  const handleGetOtp = async () => {
    if (phone.length !== 10) {
      setToast({ open: true, type: "error", message: "Please enter a valid 10-digit phone number" });
      return;
    }
    const apiKey = process.env.REACT_APP_BROWSER_API_KEY;
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!apiKey || (process.env.NODE_ENV !== "development" && !baseURL)) {
      setToast({ open: true, type: "error", message: "OTP configuration is missing. Check your environment settings." });
      return;
    }
    try {
      setLoading(true);
      setOtp(["", "", "", ""]);
      const res = await fetch(getApiUrl("send-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": apiKey },
        body: JSON.stringify({ phoneNumber: phone, phone }),
      });
      if (res.ok) {
        setOtpSent(true);
        startCountdown();
        setToast({ open: true, type: "success", message: "OTP sent successfully!" });
      } else {
        const payload = await res.json().catch(() => null);
        setToast({ open: true, type: "error", message: payload?.message || "Failed to send OTP. Please try again." });
      }
    } catch {
      setToast({ open: true, type: "error", message: "An error occurred while sending OTP." });
    } finally {
      setLoading(false);
    }
  };

  const startCountdown = () => {
    let t = 300;
    const iv = setInterval(() => { t--; setCountdown(t); if (t === 0) clearInterval(iv); }, 1000);
  };

  const handleOtpChange = (i, val) => {
    const digit = val.replace(/[^0-9]/g, "").slice(-1);
    const next = [...otp]; next[i] = digit; setOtp(next);
    if (digit && i < 3) document.getElementById(`hotp-${i + 1}`)?.focus();
  };

  const handleOtpPaste = (i, e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (!pasted) return;
    const newOtp = [...otp];
    for (let j = 0; j < pasted.length && i + j < 4; j++) newOtp[i + j] = pasted[j];
    setOtp(newOtp);
    document.getElementById(`hotp-${Math.min(i + pasted.length - 1, 3)}`)?.focus();
  };

  const handleOtpKeyDown = (i, e) => {
    if (e.key === "Backspace" && otp[i] === "" && i > 0)
      document.getElementById(`hotp-${i - 1}`)?.focus();
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 4) {
      setToast({ open: true, type: "error", message: "Please enter a valid 4-digit OTP" });
      return;
    }
    try {
      setLoading(true);
      const apiKey = process.env.REACT_APP_BROWSER_API_KEY;
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      if (!apiKey || (process.env.NODE_ENV !== "development" && !baseURL)) {
        setToast({ open: true, type: "error", message: "OTP configuration is missing." });
        return;
      }
      const res = await fetch(getApiUrl("verify-otp"), {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-api-key": apiKey },
        body: JSON.stringify({ phoneNumber: phone, phone, enteredOtp, source: "photographer_interface" }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setToast({ open: true, type: "success", message: "OTP verified successfully!" });
          if (data.userData && Object.keys(data.userData).length === 0) {
            history.push({ pathname: "/register", state: { verifiedPhone: phone } });
          } else {
            try {
              localStorage.setItem("lastPhone", phone);
              if (data.userData) {
                const { vendor_id: vid, user_id: uid, first_name, last_name, name } = data.userData;
                const displayName = `${first_name || ""} ${last_name || ""}`.trim() || name || "";
                if (vid) localStorage.setItem("vendorId", vid);
                if (uid) localStorage.setItem("uid", uid);
                if (displayName) localStorage.setItem("vendorDisplayName", displayName);
              }
            } catch {}
            history.replace("/eventlist");
          }
        } else {
          setToast({ open: true, type: "error", message: data.message || "Invalid OTP. Please try again." });
        }
      } else {
        const payload = await res.json().catch(() => null);
        setToast({ open: true, type: "error", message: payload?.message || "Failed to verify OTP." });
      }
    } catch {
      setToast({ open: true, type: "error", message: "An error occurred while verifying OTP." });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = () => {
    if (countdown === 0) {
      setIsResending(true);
      handleGetOtp();
      setTimeout(() => { setCountdown(300); setIsResending(false); }, 1000);
    }
  };

  const cellIcon = (val) => {
    if (val === true)  return <span className="ee-ck">✓</span>;
    if (val === false) return <span className="ee-cx">✗</span>;
    return <span className="ee-cp">{val}</span>;
  };

  return (
    <div className="ee">
      <Toast open={toast.open} type={toast.type} message={toast.message}
        onClose={() => setToast(t => ({ ...t, open: false }))} />

      {/* ── NAV ─────────────────────────────────────────────────────────── */}
      <nav className="ee-nav">
        <a className="ee-nav-logo" href="/">
          <img src="/images/logo.png" alt="Easy Events" className="ee-nav-logo-img" />
        </a>
        <div className="ee-nav-links">
          <a href="#how-it-works">How it works</a>
          <a href="#features">Features</a>
          <a href="#pricing">Pricing</a>
          <a href="#faq">FAQ</a>
        </div>
        <button onClick={scrollToLogin} className="ee-nav-cta">Start Free</button>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div className="ee-hero-wrap">
        <div className="ee-hero">
          <div className="ee-eyebrow">✦ AI-Powered Event Memory Platform</div>
          <h1 className="ee-h1">
            Your guests deserve more than<br />
            a <span className="ee-grad">link to a folder.</span>
          </h1>
          <p className="ee-hero-sub">
            Upload thousands of event photos once. AI finds every person's photos using a selfie,
            delivers personalized galleries instantly, and preserves every memory — forever.
          </p>
          <div className="ee-hero-btns">
            <button onClick={scrollToLogin} className="ee-btn-primary">Start Free →</button>
            <a href="#how-it-works" className="ee-btn-outline">▶ See How It Works</a>
          </div>
          <div className="ee-hero-stats">
            {[
              { val: "100K+", label: "Photos Delivered" },
              { val: "1K+",  label: "Events Managed" },
              { val: "100+", label: "Photographers" },
              { val: "98%",  label: "Guest Satisfaction" },
            ].map(s => (
              <div key={s.label} className="ee-hstat">
                <span className="ee-hstat-n">{s.val}</span>
                <span className="ee-hstat-l">{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="ee-signup-card" ref={loginRef}>
          <div className="ee-free-badge">🪙 Start with 100 free credits — no card needed</div>
          <div className="ee-sc-head">
            <div className="ee-sc-icon">e</div>
            <span className="ee-sc-title">Get Started Free</span>
          </div>
          <p className="ee-sc-sub">Your number is only used to log you in. No spam, ever.</p>

          <div className="ee-field-label">Mobile Number</div>
          <div className={`ee-field-wrap${otpSent ? " ee-field-disabled" : ""}`}>
            <div className="ee-field-prefix">+91</div>
            <input
              type="tel"
              placeholder="Enter 10-digit number"
              maxLength="10"
              value={phone}
              onChange={handleChange}
              disabled={otpSent}
            />
          </div>

          {otpSent && (
            <>
              <p className="ee-otp-hint">💬 OTP will be received on WhatsApp</p>
              <p className="ee-countdown">
                Resend OTP in {Math.floor(countdown / 60)}:{String(countdown % 60).padStart(2, '0')}{" "}
                {countdown === 0 && (
                  <span onClick={handleResendOTP} className="ee-resend">
                    {isResending ? "Sending…" : "Resend"}
                  </span>
                )}
              </p>
              <div className="ee-otp-row">
                {otp.map((d, i) => (
                  <input key={i} id={`hotp-${i}`} type="text"
                    inputMode="numeric" pattern="[0-9]*"
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    className="ee-otp-box" value={d}
                    onChange={e => handleOtpChange(i, e.target.value)}
                    onKeyDown={e => handleOtpKeyDown(i, e)}
                    onPaste={e => handleOtpPaste(i, e)} />
                ))}
              </div>
            </>
          )}

          <button className="ee-otp-btn" onClick={otpSent ? handleVerifyOtp : handleGetOtp} disabled={loading}>
            {loading && <span className="ee-spin" />}
            {loading ? (otpSent ? "Verifying…" : "Sending OTP…") : (otpSent ? "Log In" : "Get OTP")}
          </button>

        </div>
      </div>

      {/* ── TRUST ─────────────────────────────────────────────────────────── */}
      <div className="ee-trust">
        <div className="ee-trust-inner">
          <p className="ee-trust-label">Trusted by photographers, studios &amp; event organizers across India</p>
          <div className="ee-trust-pills">
            {TRUST_LOGOS.map(l => <div key={l} className="ee-tp">{l}</div>)}
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ──────────────────────────────────────────────────── */}
      <div className="ee-sec" id="how-it-works">
        <div className="ee-sec-label">Simple Process</div>
        <div className="ee-sec-title">From upload to delivery in 4 steps</div>
        <p className="ee-sec-body">What used to take days of manual sorting and coordination now happens automatically — while you focus on the next event.</p>
        <div className="ee-steps-grid">
          {STEPS.map(s => (
            <div key={s.n} className="ee-step">
              <div className="ee-step-num">Step {s.n}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── WHY US ────────────────────────────────────────────────────────── */}
      <div className="ee-bg-tinted">
        <div className="ee-sec">
          <div className="ee-sec-label">Why Photographers Choose Us</div>
          <div className="ee-sec-title">Built for your success at every event</div>
          <p className="ee-sec-body">Every feature is designed around one goal — making you look like a premium professional while cutting hours of manual work per event.</p>
          <div className="ee-why-grid">
            {WHY_CARDS.map(w => (
              <div key={w.title} className="ee-why-card">
                <div className="ee-wc-icon">{w.icon}</div>
                <h3>{w.title}</h3>
                <p>{w.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FEATURES ──────────────────────────────────────────────────────── */}
      <div className="ee-feat-section" id="features">
        <div className="ee-feat-inner">
          <div className="ee-feat-intro">
            <div className="ee-sec-label">Platform Features</div>
            <div className="ee-sec-title">Everything your event workflow needs</div>
            <p className="ee-sec-body">Not just a gallery. An integrated event ecosystem built for photographers, organizers, and every person in the photos.</p>
          </div>

          {/* Face Recognition */}
          <div className="ee-feat-block ee-feat-first">
            <div>
              <div className="ee-feat-pill">Face Recognition</div>
              <h3>Find your photos with <span className="ee-grad">one selfie</span></h3>
              <p>Guests take a single selfie and AI instantly scans the entire event — thousands of images, group shots, candids, different lighting — and delivers a personalized gallery of only their photos. No searching. No scrolling. Pure delight.</p>
              <ul className="ee-feat-pts">
                {["Scans across group photos and candid shots", "Accurate across different lighting and angles", "Results appear in seconds, not minutes", "Works at scale — 500 to 50,000 photos"].map(pt => (
                  <li key={pt}><span className="ee-pt-bullet" />{pt}</li>
                ))}
              </ul>
            </div>
            <div className="ee-phone-wrap">
              <div className="ee-phone">
                <div className="ee-phone-app-name">Easy Events</div>
                <div className="ee-cam-circle">📷</div>
                <div className="ee-phone-label">Upload your selfie</div>
                <div className="ee-phone-hint">AI will find all your photos</div>
                <div className="ee-scan-bar" />
                <div className="ee-scan-label">Scanning 4,382 photos…</div>
                <div className="ee-pgrid">
                  {FEAT_PHONE_CELLS.map((f, i) => (
                    <div key={i} className={`ee-ph${f ? " ee-ph-found" : ""}`} />
                  ))}
                </div>
                <div className="ee-found-bar">23 photos found — Tap to view</div>
              </div>
            </div>
          </div>

          {/* Smart Feed */}
          <div className="ee-feat-block ee-feat-flip">
            <div>
              <div className="ee-feat-pill">Smart Event Feed</div>
              <h3>A <span className="ee-grad">smart event feed,</span> not just a gallery</h3>
              <p>Easy Events is the only platform with a social-style event feed built in. Attendees share memories as carousel posts, interact with each other's moments, and control exactly who sees what — all with privacy logic that protects group photos automatically.</p>
              <ul className="ee-feat-pts">
                {["Feed-based sharing with carousel photo posts", "Public, Contacts-only, and Private visibility per post", "Gallery and feed stay in sync — no manual work", "Smart group photo privacy handled automatically"].map(pt => (
                  <li key={pt}><span className="ee-pt-bullet" />{pt}</li>
                ))}
              </ul>
            </div>
            <div className="ee-vcard">
              <div className="ee-vcard-hdr">Smart Event Feed</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-g" />Public post — 128 views in 2 hours</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-a" />Contacts only — shared with 24 people</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-m" />Private — only visible to you</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-g" />Group photo — privacy auto-applied</div>
              <div className="ee-vcard-note">Gallery + Feed + Privacy Engine — one platform</div>
            </div>
          </div>

          {/* Album Workflow */}
          <div className="ee-feat-block">
            <div>
              <div className="ee-feat-pill">Album Workflow</div>
              <h3>Album selection <span className="ee-grad">without the chaos</span></h3>
              <p>Clients shortlist photos for albums, prints, reels, and edits — directly inside Easy Events. Selected photos land in your workflow folder with original filenames intact. No screenshots. No WhatsApp confusion. A clean, professional handover every time.</p>
              <ul className="ee-feat-pts">
                {["Shortlist for albums, edits, prints, and highlights", "Original filenames preserved end-to-end", "Drops directly into your editing folder", "Organizer and photographer always in sync"].map(pt => (
                  <li key={pt}><span className="ee-pt-bullet" />{pt}</li>
                ))}
              </ul>
            </div>
            <div className="ee-vcard">
              <div className="ee-vcard-hdr">Album Workflow</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-g" />Album design shortlist — 43 photos selected</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-r" />Highlights reel — 12 photos confirmed</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-a" />Print order — 8 photos finalized</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-g" />Editing folder synced — original filenames</div>
              <div className="ee-vcard-note">No screenshots. No WhatsApp. Zero confusion.</div>
            </div>
          </div>

          {/* Portfolio */}
          <div className="ee-feat-block ee-feat-flip">
            <div>
              <div className="ee-feat-pill">Photographer Portfolio</div>
              <h3>Your portfolio <span className="ee-grad">inside the platform</span></h3>
              <p>Every event you deliver is a showcase. Your public photographer profile lives where attendees and organizers already are — so they can browse your portfolio, see your services, and send you an enquiry directly. Clients you served become your best advertisement.</p>
              <ul className="ee-feat-pts">
                {["Public profile with portfolio catalogue and services", "Organically discovered by attendees post-event", "Direct enquiry — no third-party tools needed", "Every event adds to your growing visibility"].map(pt => (
                  <li key={pt}><span className="ee-pt-bullet" />{pt}</li>
                ))}
              </ul>
            </div>
            <div className="ee-vcard">
              <div className="ee-vbig">
                <div className="ee-vbig-n">340</div>
                <div className="ee-vbig-s">Portfolio views this month</div>
              </div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-g" />New enquiry — Conference organizer, Bengaluru</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-g" />Booking request — Wedding, Mysuru</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-a" />Profile found via event gallery — 2 days ago</div>
              <div className="ee-vcard-note">Every event you deliver builds your next client list</div>
            </div>
          </div>

          {/* Lifetime Storage */}
          <div className="ee-feat-block">
            <div>
              <div className="ee-feat-pill">Lifetime Storage</div>
              <h3>Lifetime storage. <span className="ee-grad">Included. Always.</span></h3>
              <p>Hard drives fail. Drive links expire. Pen drives get lost. Easy Events archives every event permanently — securely stored, always accessible, always searchable. Your clients can revisit their wedding five years from now. That is the standard you are setting for them.</p>
              <ul className="ee-feat-pts">
                {["Lifetime archival included in every plan", "No expiry, no re-upload, no lost memories", "Secure access for attendees forever", "Every moment stays discoverable indefinitely"].map(pt => (
                  <li key={pt}><span className="ee-pt-bullet" />{pt}</li>
                ))}
              </ul>
            </div>
            <div className="ee-vcard">
              <div className="ee-vbig">
                <div className="ee-vbig-n">∞</div>
                <div className="ee-vbig-s">Lifetime Archive Included</div>
              </div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-g" />Wedding 2023 — accessible today, and always</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-g" />College Fest 2024 — accessible today, and always</div>
              <div className="ee-vrow"><span className="ee-dot ee-dot-g" />Conference 2025 — accessible today, and always</div>
              <div className="ee-vcard-note">Because memories deserve more than an expiry date</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── INDUSTRIES ────────────────────────────────────────────────────── */}
      <div className="ee-sec">
        <div className="ee-sec-label">Industries</div>
        <div className="ee-sec-title">Built for every event format</div>
        <div className="ee-ind-grid">
          {INDUSTRIES.map(ind => (
            <div key={ind.title} className="ee-ind-card">
              <div className="ee-ind-emoji">{ind.emoji}</div>
              <h4>{ind.title}</h4>
              <p>{ind.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── COMPARISON ────────────────────────────────────────────────────── */}
      <div className="ee-comp-wrap">
        <div className="ee-comp-inner">
          <div className="ee-sec-label">Comparison</div>
          <div className="ee-sec-title">An ecosystem, not just a gallery</div>
          <p className="ee-sec-body">Easy Events is the only platform that combines personalized delivery, social sharing, photographer workflows, and lifetime archival — all in one place.</p>
          <table className="ee-comp-table">
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Capability</th>
                <th className="ee-th-ee">Easy Events</th>
                <th>Google Drive</th>
                <th>Gallery Platforms</th>
                <th>Pen Drive</th>
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map(r => (
                <tr key={r.feature}>
                  <td>{r.feature}</td>
                  <td>{cellIcon(r.ee)}</td>
                  <td>{cellIcon(r.gd)}</td>
                  <td>{cellIcon(r.gallery)}</td>
                  <td>{cellIcon(r.pen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="ee-comp-value-note">
            <strong>On pricing:</strong> Easy Events is a complete event ecosystem — face recognition, social feed, album workflows, lifetime storage, and a portfolio that generates enquiries. When you add up what these features save you in time, tools, and repeat business, the investment pays for itself on your very first event.
          </div>
        </div>
      </div>

      {/* ── TESTIMONIALS ──────────────────────────────────────────────────── */}
      <div className="ee-testi-sec">
        <div className="ee-testi-inner">
          <div className="ee-sec-label">Testimonials</div>
          <div className="ee-sec-title">What event professionals say</div>
          <div className="ee-testi-grid">
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="ee-testi">
                <div className="ee-testi-stars">
                  {[1,2,3,4,5].map(i => <span key={i} className="ee-star" />)}
                </div>
                <div className="ee-testi-quote">"{t.text}"</div>
                <div className="ee-testi-who">
                  <div className="ee-testi-av">{t.initials}</div>
                  <div>
                    <div className="ee-testi-name">{t.name}</div>
                    <div className="ee-testi-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PRICING ───────────────────────────────────────────────────────── */}
      <div className="ee-price-sec" id="pricing">
        <div className="ee-price-inner">
          <div className="ee-sec-label">Pricing</div>
          <div className="ee-sec-title">Pay only for what you deliver</div>
          <p className="ee-price-sub">1 credit = 1 photo processed and stored. Credits deducted only on successful uploads. No monthly fees. No lock-in.</p>
          <div className="ee-price-grid">
            {PRICE_TIERS.map(tier => (
              <div key={tier.range} className={`ee-pc${tier.best ? " ee-pc-best" : ""}`}>
                <div className={`ee-pc-range${tier.best ? " ee-pc-range-best" : ""}`}>{tier.range}</div>
                <div className="ee-pc-amt">{tier.price}</div>
                <div className="ee-pc-unit">per photo + GST</div>
              </div>
            ))}
          </div>
          <div className="ee-perks-row">
            {PRICE_PERKS.map(p => <div key={p} className="ee-perk">{p}</div>)}
          </div>
          <div className="ee-free-box">
            <div>
              <h4>🪙 Start with 100 Free Credits</h4>
              <p>Every new account gets 100 credits instantly — no credit card required. Try the platform on your next event, risk-free.</p>
            </div>
            <button onClick={scrollToLogin} className="ee-free-btn">Claim Free Credits</button>
          </div>
        </div>
      </div>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <div className="ee-faq-sec" id="faq">
        <div className="ee-faq-inner">
          <div className="ee-sec-label">FAQ</div>
          <div className="ee-sec-title">Common questions</div>
          <div className="ee-faq-list">
            {FAQS.map((f, i) => (
              <div key={i} className={`ee-faq-item${openFaq === i ? " ee-faq-open" : ""}`}>
                <div className="ee-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {f.q}
                  <span className="ee-faq-arr">›</span>
                </div>
                {openFaq === i && <div className="ee-faq-a">{f.a}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── FINAL CTA ─────────────────────────────────────────────────────── */}
      <div className="ee-final-cta">
        <div className="ee-final-cta-inner">
          <div className="ee-cta-label">Get Started Today</div>
          <h2>Every event deserves to be<br />remembered forever.</h2>
          <p>Join 100+ photographers and event managers delivering AI-powered experiences that clients remember — and tell everyone about.</p>
          <div className="ee-final-btns">
            <button onClick={scrollToLogin} className="ee-fcta-card">
              <span className="ee-fcta-icon">📸</span>
              <div className="ee-fcta-title">I'm a Photographer</div>
              <div className="ee-fcta-sub">Upload photos, deliver personalized galleries, and grow your brand.</div>
              <span className="ee-fcta-arrow">Create your account →</span>
            </button>
            <button onClick={scrollToLogin} className="ee-fcta-card">
              <span className="ee-fcta-icon">🎪</span>
              <div className="ee-fcta-title">I'm an Event Manager</div>
              <div className="ee-fcta-sub">Host smarter events. Give your attendees an experience they'll share and remember forever.</div>
              <span className="ee-fcta-arrow">Create your account →</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── FOOTER ────────────────────────────────────────────────────────── */}
      <footer className="ee-footer">
        <div className="ee-footer-inner">
          <div>
            <div className="ee-fb-name">Easy Events</div>
            <div className="ee-fb-tag">AI-Powered Event Memory Platform</div>
            <div className="ee-fb-tagline">Capture. Share. Preserve Forever.</div>
          </div>
          <div className="ee-fl-col">
            <h5>Platform</h5>
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="#how-it-works">How It Works</a>
            <a href="#faq">FAQ</a>
          </div>
          <div className="ee-fl-col">
            <h5>Company</h5>
            <a href="https://easyevents.in" target="_blank" rel="noreferrer">Main Website</a>
            <a href="mailto:easyeventz21@gmail.com">Contact Us</a>
          </div>
          <div className="ee-fc-contact">
            <div className="ee-fc-label">Contact</div>
            <div>📞 +91 96329 42544</div>
            <div>🌐 easyevents.in</div>
            <div>✉ easyeventz21@gmail.com</div>
          </div>
        </div>
        <div className="ee-footer-bottom">
          <span>© 2025 Easy Events. All rights reserved.</span>
          <span>photographer.easyevents.in</span>
        </div>
      </footer>
    </div>
  );
}
